import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Chiropractor = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my chiropractor business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps chiropractor businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for chiropractor businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most chiropractor businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my chiropractor business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like chiropractor. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Chiropractor - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Chiropractor CRM", url: "/industries/chiropractor" }
  ];

  const industryData = {
    name: 'Chiropractor',
    icon: '🦴',
    title: 'Fill Your Appointment Book & Build Long-Term Patient Relationships',
    subtitle: 'Reduce no-shows, automate patient recall, and convert one-time visits into ongoing care plans',
    
    challenges: [
      'High no-show rates costing thousands in lost appointment time',
      'Patients not following through with recommended treatment plans',
      'Difficulty getting patients to book their next adjustment before leaving',
      'Manual reminder calls consuming staff time throughout the day',
      'One-time injury patients not returning for maintenance care',
      'New patient enquiries going unanswered during busy clinic hours',
      'Hard to educate patients about preventive chiropractic care benefits',
      'Not enough reviews despite excellent patient outcomes'
    ],
    
    solutions: [
      'Automated SMS and email reminders reduce no-shows from 20% to under 3%',
      'Treatment plan reminder sequences keep patients on track with their care',
      'Rebook-before-leaving automation ensures patients schedule their next visit',
      'Online booking allows new patients to schedule consultations 24/7',
      'Wellness maintenance campaigns convert injury patients into long-term care',
      'Educational email sequences explain benefits of preventive chiropractic care',
      'Review automation after treatment completion builds online reputation',
      'After-hours emergency form captures urgent cases with automatic response'
    ],
    
    features: [
      '24/7 Online Appointment Booking',
      'Automated SMS & Email Reminders (48hr, 24hr, 2hr)',
      'Treatment Plan Progress Tracking',
      'Rebook Automation Before Patient Leaves',
      'Wellness Maintenance Recall Campaigns',
      'New Patient Welcome Sequences',
      'Chiropractic Education Email Series',
      'After-Hours Emergency Request Form',
      'Insurance Information Collection',
      'Google Review Request After Treatment',
      'Patient Portal with Visit History',
      'Cancellation Waitlist Management'
    ],
    
    results: [
      'Reduce no-shows by 85%',
      '78% of patients complete treatment plans',
      '82% rebook before leaving clinic',
      'Convert 45% to wellness plans',
      'Fill appointment book 95%+',
      'Average 4.8★ Google rating'
    ],
    
    reviews: [
      {
        author: 'Dr. Mark Thompson',
        location: 'Manchester',
        text: 'The no-show reduction has been remarkable. We went from 18% no-shows costing us £2,400/month to under 3%. The automated reminders work perfectly and patients appreciate them. That\'s £28,000 saved in the first year alone.',
        result: '£28,000 recovered revenue annually'
      },
      {
        author: 'Dr. Sarah Chen',
        location: 'Birmingham',
        text: 'The treatment plan tracking has improved patient outcomes dramatically. Patients get automated reminders about their adjustment schedule and 78% now complete their full treatment plan versus 45% before. Better outcomes mean more referrals.',
        result: '78% treatment plan completion rate'
      },
      {
        author: 'Dr. James Wilson',
        location: 'Leeds',
        text: 'The rebook-before-leaving automation ensures patients schedule their next visit before walking out. My retention has gone from 60% to 82%. That\'s consistent appointments and predictable income instead of chasing patients.',
        result: '82% patient retention rate'
      },
      {
        author: 'Dr. Emma Roberts',
        location: 'Bristol',
        text: 'The wellness maintenance campaigns have built me a solid base of ongoing patients. 45% of my injury patients have converted to monthly wellness plans. That\'s stable recurring revenue and healthier patients.',
        result: '45% convert to wellness maintenance'
      },
      {
        author: 'Dr. Michael Brown',
        location: 'Liverpool',
        text: 'Online booking has been fantastic for new patient acquisition. They can book initial consultations at midnight or during work breaks. 60% of my new patients now come through online booking with no phone calls required.',
        result: '60% of new patients book online'
      },
      {
        author: 'Dr. Rachel Foster',
        location: 'Glasgow',
        text: 'The review automation got us from 15 reviews to 143 in 10 months. We\'re now the top-rated chiropractor in Glasgow. New patients specifically mention our reviews and the educational content we send. Enquiries up 75%.',
        result: '143 reviews, 75% more enquiries'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Chiropractor UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for chiropractor businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for chiropractor UK, chiropractor business software, chiropractor customer management, chiropractor lead generation UK"
        url="/industries/chiropractor"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Chiropractor;